// Layout-Primitive (DESIGN §3.17, §2.5): Abstände nur aus der Skala 4 · 8 · 12 · 16 · 20 · 24 · 32 · 40 · 48 · 64.
import { type ComponentPropsWithRef, type ElementType, type ReactNode } from 'react';
import { cn } from './cn';
import { cva, type VariantProps } from './cva';

/** Erlaubte Abstandsstufen in px (DESIGN §2.5). */
export type Space = 0 | 4 | 8 | 12 | 16 | 20 | 24 | 32 | 40 | 48 | 64;

const GAP: Record<Space, string> = {
  0: 'gap-0',
  4: 'gap-1',
  8: 'gap-2',
  12: 'gap-3',
  16: 'gap-4',
  20: 'gap-5',
  24: 'gap-6',
  32: 'gap-8',
  40: 'gap-10',
  48: 'gap-12',
  64: 'gap-16',
};

const ALIGN = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
  baseline: 'items-baseline',
} as const;
const JUSTIFY = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  between: 'justify-between',
} as const;

type BoxProps<E extends ElementType> = {
  as?: E;
  children?: ReactNode;
} & Omit<ComponentPropsWithRef<E>, 'as' | 'children'>;

export type StackProps<E extends ElementType = 'div'> = BoxProps<E> & {
  gap?: Space;
  align?: keyof typeof ALIGN;
  justify?: keyof typeof JUSTIFY;
};

/** Senkrechter Stapel mit festem Abstand. */
export function Stack<E extends ElementType = 'div'>({
  as,
  gap = 16,
  align,
  justify,
  className,
  ...rest
}: StackProps<E>) {
  const Component: ElementType = as ?? 'div';
  return (
    <Component
      className={cn(
        'flex min-w-0 flex-col',
        GAP[gap],
        align && ALIGN[align],
        justify && JUSTIFY[justify],
        className,
      )}
      {...rest}
    />
  );
}

export type ClusterProps<E extends ElementType = 'div'> = StackProps<E> & { wrap?: boolean };

/** Waagerechte Reihe (Pills, Chips, Aktionen), bricht standardmäßig um. */
export function Cluster<E extends ElementType = 'div'>({
  as,
  gap = 8,
  align = 'center',
  justify,
  wrap = true,
  className,
  ...rest
}: ClusterProps<E>) {
  const Component: ElementType = as ?? 'div';
  return (
    <Component
      className={cn(
        'flex min-w-0',
        wrap ? 'flex-wrap' : 'flex-nowrap',
        GAP[gap],
        ALIGN[align],
        justify && JUSTIFY[justify],
        className,
      )}
      {...rest}
    />
  );
}

const gridVariants = cva('grid min-w-0', {
  variants: {
    cols: {
      1: 'grid-cols-1',
      2: 'grid-cols-1 sm:grid-cols-2',
      3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
      4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
      6: 'grid-cols-2 md:grid-cols-3 xl:grid-cols-6',
      auto: 'grid-cols-[repeat(auto-fill,minmax(min(100%,240px),1fr))]',
    },
  },
  defaultVariants: { cols: 2 },
});

export type GridProps<E extends ElementType = 'div'> = BoxProps<E> &
  VariantProps<typeof gridVariants> & {
    /** Standard 16, mobil 12 (DESIGN §1.4). */
    gap?: Space;
  };

/** Raster mit responsiven Spalten (mobil einspaltig, ab `sm` zwei …). */
export function Grid<E extends ElementType = 'div'>({
  as,
  cols,
  gap,
  className,
  ...rest
}: GridProps<E>) {
  const Component: ElementType = as ?? 'div';
  return (
    <Component
      className={cn(
        gridVariants({ cols }),
        gap === undefined ? 'gap-3 md:gap-4' : GAP[gap],
        className,
      )}
      {...rest}
    />
  );
}

export interface TwoColumnProps extends Omit<ComponentPropsWithRef<'div'>, 'children'> {
  main: ReactNode;
  aside: ReactNode;
  /** Beschriftung der rechten Spalte für Screenreader (wird zu `aside`). */
  asideLabel?: string;
}

/** Hauptspalte + rechte Spalte (lg 300, xl 343); darunter einspaltig mit der Spalte unter dem Inhalt. */
export function TwoColumn({ main, aside, asideLabel, className, ...rest }: TwoColumnProps) {
  return (
    <div
      className={cn(
        'grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-6 xl:grid-cols-[minmax(0,1fr)_var(--spacing-col-right)]',
        className,
      )}
      {...rest}
    >
      <div className="flex min-w-0 flex-col gap-3 md:gap-4">{main}</div>
      <aside aria-label={asideLabel} className="flex min-w-0 flex-col gap-3 md:gap-4">
        {aside}
      </aside>
    </div>
  );
}

const containerVariants = cva('mx-auto w-full min-w-0', {
  variants: {
    width: {
      page: 'max-w-page-max',
      read: 'max-w-ai-read',
      wide: 'max-w-ai-wide',
      auth: 'max-w-auth',
      full: '',
    },
  },
  defaultVariants: { width: 'page' },
});

export type PageContainerProps<E extends ElementType = 'div'> = BoxProps<E> &
  VariantProps<typeof containerVariants>;

/** Begrenzt die Inhaltsbreite (People/Admin 1440, Bar AI 760 bzw. 1200). Ränder setzt die Shell. */
export function PageContainer<E extends ElementType = 'div'>({
  as,
  width,
  className,
  ...rest
}: PageContainerProps<E>) {
  const Component: ElementType = as ?? 'div';
  return <Component className={containerVariants({ width, className })} {...rest} />;
}
